import LeaseRecordInput from 'src/components/leases/LeaseRecordInput';
import React from 'react';
import { ILeaseMaster } from 'src/models/dlcLease/ILeaseMaster';
import { ILeaseLessorDetails } from 'src/models/dlcLease/ILeaseLessorDetails';
import * as styles from './styles';
import LessorCsvDownloadButton from './LessorCsvDownloadButton';

interface ILessorSectionProps {
    leaseMaster: ILeaseMaster;
    onChange?: (field: string, value: unknown) => void;
}

const LessorSection: React.FC<ILessorSectionProps> = ({ leaseMaster, onChange }) => {
    const lessorDetails: ILeaseLessorDetails | undefined = leaseMaster.lessorDetails;
    const isEditable = !!onChange;
    const reportableInvoices = (leaseMaster.invoices || []).filter((invoice) => invoice.operation !== 'delete' && !!invoice.liTg);
    const [selectedLiTg, setSelectedLiTg] = React.useState<number | undefined>();
    const selectedLiTgIsValid = !!selectedLiTg && reportableInvoices.some((invoice) => invoice.liTg === selectedLiTg);
    const effectiveLiTg = selectedLiTgIsValid ? selectedLiTg : reportableInvoices.length === 1 ? reportableInvoices[0].liTg : undefined;
    const subsectionCardStyle: React.CSSProperties = {
        padding: '0.75rem',
        backgroundColor: styles.colors.gray50,
        borderRadius: '6px',
        border: `1px solid ${styles.colors.gray200}`,
    };

    const handleChange = (field: string, value: string) => {
        if (onChange) {
            onChange(`lessorDetails.${field}`, value);
        }
    };

    return (
        <div style={styles.sectionContainer}>
            <h2 style={styles.sectionHeader}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={styles.colors.primary} strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                    </svg>
                    Lessor Information
                </span>
            </h2>

            <div style={{ ...subsectionCardStyle, marginBottom: '0.75rem' }}>
                <h3 style={{ margin: '0 0 0.5rem 0', fontSize: styles.typography.fontSize.sm, color: styles.colors.gray800 }}>
                    Form-letter data file
                </h3>
                {reportableInvoices.length ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                        <label style={styles.fieldLabel} htmlFor="lessor-report-invoice">
                            Invoice / LI_TG
                        </label>
                        <select
                            id="lessor-report-invoice"
                            value={effectiveLiTg || ''}
                            onChange={(event) => setSelectedLiTg(event.target.value ? Number(event.target.value) : undefined)}
                            style={styles.inputBase}
                        >
                            <option value="">Select an invoice</option>
                            {reportableInvoices.map((invoice) => (
                                <option key={invoice.liTg} value={invoice.liTg}>
                                    Invoice {invoice.invoiceNo1 || 'not numbered'} — LI_TG {invoice.liTg}
                                </option>
                            ))}
                        </select>
                        <LessorCsvDownloadButton liTg={effectiveLiTg} />
                    </div>
                ) : (
                    <p style={{ margin: 0, color: styles.colors.gray600, fontSize: styles.typography.fontSize.sm }}>
                        Save an invoice to assign an LI_TG before downloading the Lessor data file.
                    </p>
                )}
            </div>

            {lessorDetails ? (
                <>
                    {/* Names Section */}
                    <div
                        style={{
                            marginBottom: '0.75rem',
                            ...subsectionCardStyle,
                        }}
                    >
                        <h3
                            style={{
                                margin: '0 0 0.5rem 0',
                                fontSize: styles.typography.fontSize.sm,
                                fontWeight: styles.typography.fontWeight.semibold,
                                color: styles.colors.gray800,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                            }}
                        >
                            Names
                        </h3>
                        <div style={styles.gridCols3}>
                            <Field
                                label="Primary Name"
                                value={lessorDetails.name1 || ''}
                                editable={isEditable}
                                onChange={(value) => handleChange('name1', value)}
                                required={true}
                            />
                            {(lessorDetails.name2 || isEditable) && (
                                <Field
                                    label="Secondary Name"
                                    value={lessorDetails.name2 || ''}
                                    editable={isEditable}
                                    onChange={(value) => handleChange('name2', value)}
                                />
                            )}
                            {(lessorDetails.name3 || isEditable) && (
                                <Field
                                    label="Additional Name"
                                    value={lessorDetails.name3 || ''}
                                    editable={isEditable}
                                    onChange={(value) => handleChange('name3', value)}
                                />
                            )}
                            <Field
                                label="Salutation"
                                value={lessorDetails.sal || ''}
                                editable={isEditable}
                                onChange={(value) => handleChange('sal', value)}
                                placeholder="Mr./Mrs./Ms."
                            />
                        </div>
                    </div>

                    <div
                        style={{
                            marginBottom: '0.75rem',
                            display: 'grid',
                            gap: '0.75rem',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))',
                        }}
                    >
                        {/* Contact Information */}
                        <div
                            style={{
                                ...subsectionCardStyle,
                            }}
                        >
                            <h3
                                style={{
                                    margin: '0 0 0.5rem 0',
                                    fontSize: styles.typography.fontSize.sm,
                                    fontWeight: styles.typography.fontWeight.semibold,
                                    color: styles.colors.gray800,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                }}
                            >
                                Contact Information
                            </h3>
                            <div style={styles.gridCols2}>
                                <Field
                                    label="Home Phone"
                                    value={lessorDetails.hphone || ''}
                                    editable={isEditable}
                                    onChange={(value) => handleChange('hphone', value)}
                                    type="tel"
                                    autoComplete="off"
                                />
                                <Field
                                    label="Work Phone"
                                    value={lessorDetails.wphone || ''}
                                    editable={isEditable}
                                    onChange={(value) => handleChange('wphone', value)}
                                    type="tel"
                                    autoComplete="off"
                                />
                                <Field
                                    label="SSN / Tax ID"
                                    value={lessorDetails.ssn || ''}
                                    editable={isEditable}
                                    onChange={(value) => handleChange('ssn', value)}
                                    type="text"
                                    autoComplete="off"
                                    name="prevent-autofill-ssn"
                                    data-form-type="other"
                                />
                            </div>
                        </div>

                        {/* Address Information */}
                        <div
                            style={{
                                ...subsectionCardStyle,
                            }}
                        >
                            <h3
                                style={{
                                    margin: '0 0 0.5rem 0',
                                    fontSize: styles.typography.fontSize.sm,
                                    fontWeight: styles.typography.fontWeight.semibold,
                                    color: styles.colors.gray800,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                }}
                            >
                                Address
                            </h3>
                            <div style={{ display: 'grid', gap: '0.5rem' }}>
                                <Field
                                    label="Address Line 1"
                                    value={lessorDetails.addr1 || ''}
                                    editable={isEditable}
                                    onChange={(value) => handleChange('addr1', value)}
                                />
                                {(lessorDetails.addr2 || isEditable) && (
                                    <Field
                                        label="Address Line 2"
                                        value={lessorDetails.addr2 || ''}
                                        editable={isEditable}
                                        onChange={(value) => handleChange('addr2', value)}
                                        placeholder="Apt, Suite, Unit, etc."
                                    />
                                )}
                                <Field
                                    label="City, State, Zip"
                                    value={lessorDetails.cstz || ''}
                                    editable={isEditable}
                                    onChange={(value) => handleChange('cstz', value)}
                                    placeholder="City, ST 12345"
                                />
                            </div>
                        </div>
                    </div>
                </>
            ) : (
                <div style={styles.emptyState}>
                    <svg
                        width="32"
                        height="32"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={styles.colors.gray400}
                        strokeWidth="1.5"
                        style={{ margin: '0 auto 0.5rem' }}
                    >
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                        <line x1="12" y1="14" x2="12" y2="18" />
                    </svg>
                    <p style={{ margin: 0, color: styles.colors.gray600, fontSize: styles.typography.fontSize.sm }}>No lessor details available</p>
                    {isEditable && (
                        <p style={{ marginTop: '0.5rem', fontSize: styles.typography.fontSize.sm, color: styles.colors.gray500 }}>
                            Add lessor information to complete this section
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

const Field = ({
    label,
    value,
    editable = false,
    onChange,
    type = 'text',
    placeholder,
    required = false,
    autoComplete = 'off',
    name,
    'data-form-type': dataFormType,
}: {
    label: string;
    value: string;
    editable?: boolean;
    onChange?: (value: string) => void;
    type?: string;
    placeholder?: string;
    required?: boolean;
    autoComplete?: string;
    name?: string;
    'data-form-type'?: string;
}) => (
    <div style={styles.fieldContainer}>
        <label
            style={{
                ...styles.fieldLabel,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
            }}
        >
            {label}
            {required && <span style={{ color: styles.colors.danger }}>*</span>}
        </label>
        <LeaseRecordInput
            type={type}
            value={value}
            disabled={!editable}
            onChange={(e) => onChange?.(e.target.value)}
            placeholder={editable ? placeholder : ''}
            autoComplete={autoComplete}
            name={name}
            data-form-type={dataFormType}
            onFocus={(e) => {
                if (editable) {
                    e.currentTarget.style.borderColor = styles.colors.borderFocus;
                    e.currentTarget.style.boxShadow = styles.inputFocus.boxShadow;
                }
            }}
            onBlur={(e) => {
                e.currentTarget.style.borderColor = styles.colors.gray300;
                e.currentTarget.style.boxShadow = 'none';
            }}
            style={editable ? styles.inputBase : styles.inputDisabled}
        />
    </div>
);

export default LessorSection;



// WEBPACK FOOTER //
// ./src/components/leases/sections/Lessor.tsx